import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Building2, ArrowLeft, Percent } from 'lucide-react';
import { InventoryPage } from '../inventory/InventoryChrome';
import { Card, CardHeader, Kpi, Chip, Note, Empty, RubyModal } from '../../components/finance/financeUi';
import { money } from '../../components/finance/financeFormat';
import { RubyLoader } from '../../components/RubyLoader';
import SearchableDropdown from '../../components/SearchableDropdown';
import { DatePicker } from '../../components/DatePicker';
import { feeOverrideBody } from './feeOverrideBody';
import {
  estateAdminApi, type BusinessDetail, type FeePlan, type FeeSource,
} from '../../services/estateAdminApi';

const SOURCE_LABEL: Record<FeeSource, string> = {
  override: 'Override', deal: 'Deal', default: 'Default', platform: 'Platform',
};

const pct = (bps: number): string => `${(bps / 100).toFixed(2)}%`;

const EstateBusinessDetailPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<BusinessDetail | null>(null);
  const [plans, setPlans] = useState<FeePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overridePlanId, setOverridePlanId] = useState<string | null>(null);
  const [overrideUntil, setOverrideUntil] = useState('');
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);

  const [clearOpen, setClearOpen] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);

  const load = () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    Promise.all([estateAdminApi.business(tenantId), estateAdminApi.feePlans()])
      .then(([d, p]) => { setDetail(d); setPlans(p.items || []); })
      .catch((e) => setError(e?.message || 'Could not load this business'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [tenantId]);

  if (!tenantId) return null;

  const openOverride = () => {
    setOverridePlanId(detail?.resolvedFee.source === 'override' ? (detail.resolvedFee.planId || null) : null);
    setOverrideUntil(detail?.resolvedFee.source === 'override' ? (detail.resolvedFee.until || '') : '');
    setOverrideError(null);
    setOverrideOpen(true);
  };

  const submitOverride = async () => {
    if (!overridePlanId) { setOverrideError('Choose a fee plan.'); return; }
    setOverrideBusy(true);
    setOverrideError(null);
    try {
      await estateAdminApi.setFeeOverride(tenantId, feeOverrideBody(overridePlanId, overrideUntil));
      setOverrideOpen(false);
      load();
    } catch (e: any) {
      setOverrideError(e?.message || 'Could not set the override');
    } finally {
      setOverrideBusy(false);
    }
  };

  const submitClear = async () => {
    setClearBusy(true);
    try {
      await estateAdminApi.setFeeOverride(tenantId, { planId: null });
      setClearOpen(false);
      load();
    } catch (e: any) {
      setError(e?.message || 'Could not clear the override');
    } finally {
      setClearBusy(false);
    }
  };

  return (
    <InventoryPage icon={Building2} title={detail?.stats.businessName || tenantId} subtitle="Ruby Estate business" crumb="Ruby Estate">
      <button
        type="button"
        onClick={() => navigate('/estate-admin/businesses')}
        className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 mb-4 min-h-[44px]"
      >
        <ArrowLeft size={15} /> Back to Landlords
      </button>

      {error && <Note tone="danger">{error}</Note>}

      {loading || !detail ? (
        <RubyLoader variant="page" label="Loading business detail" />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Units" value={String(detail.stats.units)} sub={`${detail.stats.occupied} occupied`} />
            <Kpi label="Residents" value={String(detail.stats.residents)} sub={`${detail.stats.residentsWithLogin} signed in`} />
            <Kpi label="Active leases" value={String(detail.stats.activeLeases)} />
            <Kpi label="Open work orders" value={String(detail.stats.openWorkOrders)} tone={detail.stats.openWorkOrders ? 'warn' : 'default'} />
          </div>

          <Card>
            <CardHeader
              icon={<Percent size={16} />}
              title="Fee plan"
              actions={(
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={openOverride}
                    className="px-3 py-2 rounded-lg bg-stone-900 text-white text-xs font-semibold hover:bg-black min-h-[44px]"
                  >
                    Set override
                  </button>
                  {detail.resolvedFee.source === 'override' && (
                    <button
                      type="button"
                      onClick={() => setClearOpen(true)}
                      className="px-3 py-2 rounded-lg bg-stone-100 text-stone-700 text-xs font-semibold hover:bg-stone-200 min-h-[44px]"
                    >
                      Clear override
                    </button>
                  )}
                </div>
              )}
            />
            <div className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Chip tone={detail.resolvedFee.source === 'override' ? 'blue' : detail.resolvedFee.source === 'deal' ? 'violet' : 'stone'}>
                  {SOURCE_LABEL[detail.resolvedFee.source]}
                  {detail.resolvedFee.source === 'override' && detail.resolvedFee.until ? ` until ${detail.resolvedFee.until}` : ''}
                </Chip>
                {detail.resolvedFee.planId && <span className="text-xs text-stone-500">{detail.resolvedFee.planId}</span>}
              </div>
              <div className="text-sm text-stone-700">
                ACH {pct(detail.resolvedFee.config.achBps)} + {money(detail.resolvedFee.config.achFlatCents)}
                <span className="text-stone-400 mx-2">·</span>
                Card {pct(detail.resolvedFee.config.cardBps)} + {money(detail.resolvedFee.config.cardFlatCents)}
                <span className="text-stone-400 mx-2">·</span>
                Min {money(detail.resolvedFee.config.minCents)}
                <span className="text-stone-400 mx-2">·</span>
                Max {detail.resolvedFee.config.maxCents === null ? 'No max' : money(detail.resolvedFee.config.maxCents)}
              </div>
            </div>
          </Card>

          {detail.deal && (
            <Card>
              <CardHeader title="Deal" />
              <div className="p-4 text-sm text-stone-700 space-y-1">
                <div>{detail.deal.name || 'Unnamed deal'}</div>
                {detail.deal.estateFeePlanId && (
                  <div className="text-stone-500">
                    Fee plan {detail.deal.estateFeePlanId}
                    {detail.deal.estateFeeMonths ? ` for ${detail.deal.estateFeeMonths} months` : ' for the life of the deal'}
                    {' — '}
                    <Chip tone={detail.deal.active === false ? 'stone' : 'emerald'}>{detail.deal.active === false ? 'Expired' : 'Active'}</Chip>
                  </div>
                )}
              </div>
            </Card>
          )}

          <Card>
            <CardHeader title="Recent fee records" />
            {detail.feeRecords.length === 0 ? (
              <Empty icon={<Percent size={28} />} title="No fee records yet" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-stone-500 border-b border-stone-200">
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Method</th>
                      <th className="px-4 py-2">Amount</th>
                      <th className="px-4 py-2">Fee</th>
                      <th className="px-4 py-2">Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.feeRecords.slice(0, 5).map((r) => (
                      <tr key={r.sk} className="border-b border-stone-100 last:border-0">
                        <td className="px-4 py-2 text-stone-600">{r.date}</td>
                        <td className="px-4 py-2 text-stone-600 uppercase">{r.method}</td>
                        <td className="px-4 py-2 tabular-nums text-stone-800">{money(r.amountCents)}</td>
                        <td className="px-4 py-2 tabular-nums text-stone-800">{money(r.feeCents)}</td>
                        <td className="px-4 py-2 text-stone-500">{r.feePlanId || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card>
            <CardHeader title="Recent failures" />
            {detail.failures.length === 0 ? (
              <Empty icon={<Percent size={28} />} title="No failures" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-stone-500 border-b border-stone-200">
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.failures.slice(0, 5).map((f) => (
                      <tr key={f.sk} className="border-b border-stone-100 last:border-0">
                        <td className="px-4 py-2 text-stone-600">{f.date}</td>
                        <td className="px-4 py-2 text-stone-700">{f.reason || 'Unknown'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      <RubyModal
        open={overrideOpen}
        icon={<Percent size={16} style={{ color: '#DC143C' }} />}
        title="Set fee override"
        onClose={() => setOverrideOpen(false)}
        confirmLabel={overrideBusy ? 'Saving…' : 'Set override'}
        onConfirm={submitOverride}
        busy={overrideBusy}
      >
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Fee plan</label>
          <SearchableDropdown
            options={plans.filter((p) => !p.archived).map((p) => ({ value: p.planId, label: p.name }))}
            value={overridePlanId}
            onChange={setOverridePlanId}
            placeholder="Choose a plan…"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Ends on (optional)</label>
          <DatePicker value={overrideUntil} onChange={setOverrideUntil} placeholder="No end date" />
          {/* Small-fixes item 23: the picker has no clear, so the end date is removed here (same plan, no end). */}
          {overrideUntil && (
            <button
              type="button"
              onClick={() => setOverrideUntil('')}
              className="mt-1 text-xs font-medium text-stone-600 underline hover:text-stone-900"
              style={{ minHeight: 44 }}
            >
              Remove end date
            </button>
          )}
        </div>
        {overrideError && <Note tone="danger">{overrideError}</Note>}
      </RubyModal>

      <RubyModal
        open={clearOpen}
        icon={<Percent size={16} style={{ color: '#DC143C' }} />}
        title="Clear fee override?"
        onClose={() => setClearOpen(false)}
        confirmLabel="Clear override"
        onConfirm={submitClear}
        busy={clearBusy}
      >
        <p>This business will fall back to its deal's fee plan, or the platform default if it has none.</p>
      </RubyModal>
    </InventoryPage>
  );
};

export default EstateBusinessDetailPage;
